"use client";

import Alert from "@/components/design-system/alert";
import Button from "@/components/design-system/button";
import Dropdown from "@/components/design-system/dropdown";
import Modal from "@/components/design-system/modal";
import TextInputField from "@/components/design-system/textInputField";
import Toast from "@/components/design-system/toast";
import Tooltip from "@/components/design-system/tooltip";
import type { SellProductEligibility } from "@/fetch/getSellProductEligibility";
import { useAddVendorProductMutation } from "@/store/productApi";
import { hideSellThisNowToast, showSellThisNowToast } from "@/store/sellThisNowToastSlice";
import { AppState } from "@/store/store";
import { UserState, useGetUserQuery } from "@/store/userApi";
import { useLazyGetFulfillmentPolicyDefaultsQuery } from "@/store/vendorApi";
import { default as Big, default as BigDecimal } from "big.js";
import delay from "delay";
import InfoIcon from "public/next-public/common/icons/info-icon.svg";
import React, { FunctionComponent, useEffect, useRef, useState } from "react";
import { NumericFormat } from "react-number-format";
import { useDispatch, useSelector } from "react-redux";
import styles from "./sellThisNowModal.module.scss";

type Props = {
  mpId: number;
  vendorOptions: VendorOptionState[];
  retailPrice: number;
  /**
   * Server-fetched eligibility (getSellProductEligibility on the PDP). null when the user is
   * anonymous / not a vendor admin / the fetch failed — the control renders nothing in that case.
   */
  eligibility: SellProductEligibility | null;
  onSubmitSuccess?: () => void;
  /** Size of the "Want to sell this product?" control. */
  triggerSize?: "large" | "small";
};
const SellThisNow: FunctionComponent<Props> = ({
  mpId,
  vendorOptions,
  retailPrice,
  eligibility,
  onSubmitSuccess,
  triggerSize = "large",
}) => {
  const { data: user } = useGetUserQuery();
  const [getFulfillmentPolicyDefaultsResponse] = useLazyGetFulfillmentPolicyDefaultsQuery();
  const [addVendorProduct] = useAddVendorProductMutation();
  const dispatch = useDispatch();
  const showSuccessToast = useSelector((state: AppState) => state.sellThisNowToastSlice.showToast);

  // Derived from the server eligibility response, preserving the legacy if/else-if precedence:
  // the inline "add product" form (no existing listing) wins over the OMS link.
  const enableSellThisNowFeature = eligibility?.showSellProduct === true && eligibility?.vendorProductExist === false;
  const enableSellThisNowLinkToOMS =
    !enableSellThisNowFeature &&
    eligibility?.showSellProduct === true &&
    (eligibility?.vendorProductExist === true || eligibility?.vendorProductActive === false);

  const [showSellThisNowModal, setShowSellThisNowModal] = useState<boolean>(false);
  const [submittingToBackend, setSubmittingToBackend] = useState<boolean>(false);
  const [fulfillmentPolicyMap, setFulfillmentPolicy] = useState(new Map<FulfillmentPolicy, FulfillmentPolicyValue>());
  const [chosenFulfillmentPolicy, setChosenFulfillmentPolicy] = useState<FulfillmentPolicy>(FulfillmentPolicy.STOCKED);
  // Prefilled with the existing listing's code for the OMS-link case; empty ("") for the
  // add-product form (no existing listing ⇒ vendorProductCode is null), where the vendor types it.
  const [vendorProductCode, setVendorProductCode] = useState<string>(eligibility?.vendorProductCode ?? "");
  const [showVPCodeError, setShowVPCodeError] = useState<VPCodeError>("none");
  const [priceBreak1MinQty, setPriceBreak1MinQty] = useState<string>("");
  const [showPB1MinQtyError, setShowPB1MinQtyError] = useState<PriceBreakMinQtyErrorTypes>("none");
  const [priceBreak1UnitPrice, setPriceBreak1UnitPrice] = useState<string>("");
  const [showPB1UnitPriceError, setShowPB1UnitPriceError] = useState<PriceBreak1UnitPriceErrorTypes>("none");
  const [priceBreak2MinQty, setPriceBreak2MinQty] = useState<string>("");
  const [showPB2MinQtyError, setShowPB2MinQtyError] = useState<
    Omit<PriceBreakMinQtyErrorTypes, "out-of-bounds"> | "minqty2-lower-than-minqty1"
  >("none");
  const [priceBreak2UnitPrice, setPriceBreak2UnitPrice] = useState<string>("");
  const [showPB2UnitPriceError, setShowPB2UnitPriceError] = useState<PriceBreak2UnitPriceErrorTypes>("none");
  const [inventory, setInventory] = useState<string>("");
  const [showInventoryError, setShowInventoryError] = useState<InventoryError>("none");
  const [promisedHandlingTime, setPromisedHandlingTime] = useState<string>("");
  const [editDefaultPHT, setEditDefaultPHT] = useState<boolean>(false);
  const [promisedHandlingTimeError, setPromisedHandlingTimeError] = useState<PHTError>("none");
  const [showSessionError, setShowSessionError] = useState<boolean>(false);
  const [showUnknownError, setShowUnknownError] = useState<boolean>(false);
  const [submissionSuccessful, setSubmissionSuccessful] = useState<boolean>(false);
  const [formWasValidated, setFormWasValidated] = useState<boolean>(false);
  const [confirmationForGreaterThan10MinQty1, setConfirmationForGreaterThan10MinQty1] = useState<boolean>(false);
  const [confirmationForGreaterThan25PercPrice1, setConfirmationForGreaterThan25PercPrice1] = useState<boolean>(false);
  const [confirmationForGreaterThan25PercPrice2, setConfirmationForGreaterThan25PercPrice2] = useState<boolean>(false);
  const [duplicateVPCodeProductInfo, setDuplicateVPCodeProductInfo] = useState<DuplicateProductInfo | null>(null);
  const [preventDefaultPHTFromDisplaying, setPreventDefaultPHTFromDisplaying] = useState<boolean>(false);

  const sellThisNowFormRef = useRef<HTMLFormElement>(null);

  const getFulfillmentPolicyDefaults = async (user?: UserState, timesAttempted = 0) => {
    try {
      // Note that the following should not occur.  However there is a chance of a race condition, so this will retry.
      const vendorId = user && user.IsGuestUser === false ? user.VendorId : undefined;
      if ((user?.IsGuestUser === true || vendorId === undefined) && timesAttempted <= 5) {
        await delay(500);
        getFulfillmentPolicyDefaults(user, timesAttempted + 1);
        return;
      } else if (user?.IsGuestUser === true || vendorId === undefined) {
        console.log("getFulfillmentPolicyDefaults could not process due to current user not being a vendor");
        setEditDefaultPHT(true);
        setPreventDefaultPHTFromDisplaying(true);
        return;
      }
      const fulfillmentPolicyDefaults = await getFulfillmentPolicyDefaultsResponse(vendorId).unwrap();
      const newFulfillmentPolicyMap = new Map<FulfillmentPolicy, FulfillmentPolicyValue>();
      fulfillmentPolicyDefaults?.forEach((rawFulfillmentPolicy) => {
        const fp = {
          id: rawFulfillmentPolicy.fulfillmentPolicyId,
          defaultPromisedHandlingTime: rawFulfillmentPolicy.promisedHandlingTime,
          promisedHandlingTimeMinimum: rawFulfillmentPolicy.minPromisedHandlingTime,
          promisedHandlingTimeMaximum: rawFulfillmentPolicy.maxPromisedHandlingTime,
        };
        newFulfillmentPolicyMap.set(rawFulfillmentPolicy.fulfillmentPolicyId, fp);
      });
      setFulfillmentPolicy(newFulfillmentPolicyMap);
    } catch (error) {
      console.error(`Sell This Now getFulfillmentPolicyDefaults() threw an exception:`, error);
      setEditDefaultPHT(true);
      setPreventDefaultPHTFromDisplaying(true);
    }
  };

  // Eligibility is now resolved server-side and arrives via props. When it enables the inline
  // add-product form, prefetch the fulfillment-policy defaults once the client-side user (whose
  // VendorId the vendorApi query needs) has been retrieved.
  useEffect(() => {
    if (enableSellThisNowFeature && user?.userHasBeenRetrieved) {
      getFulfillmentPolicyDefaults(user);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enableSellThisNowFeature, user?.userHasBeenRetrieved]);

  /**
   * This useEffect will attempt to resubmit on confirming that the vendor is okay with the various 25%
   * price issues or minQty issues.  The original interpretation of how this would work is that they'd confirm
   * and then hit submit, but this was a mistake.  To keep readability of the code below, I've decided
   * to use this useEffect instead of adding yet another prop to the ConfirmButton component defined below.
   */
  useEffect(() => {
    const isValidUser = !user?.IsGuestUser && user?.isAuthorized === true && user?.IsVendorAdmin === true;
    if (
      isValidUser &&
      (confirmationForGreaterThan10MinQty1 ||
        confirmationForGreaterThan25PercPrice1 ||
        confirmationForGreaterThan25PercPrice2)
    ) {
      submitToBackend();
    }
  }, [
    confirmationForGreaterThan10MinQty1,
    confirmationForGreaterThan25PercPrice1,
    confirmationForGreaterThan25PercPrice2,
  ]);

  if (enableSellThisNowLinkToOMS) {
    return (
      <div id="sell-this-now">
        <Button
          variant="tertiary"
          color="blue"
          size={triggerSize}
          text="Want to sell this product?"
          showLeftIcon={false}
          showRightIcon={false}
          link={{
            href: `/ncs/vendor-product-management?searchQuery=${vendorProductCode}`,
          }}
        />
      </div>
    );
  } else if (!enableSellThisNowFeature) {
    return null;
  }

  const vpCodeErrorMessage = (() => {
    switch (showVPCodeError) {
      case "invalid-format":
        return (
          <>
            Enter a valid VP code. May contain letters, numbers, hyphen and space. May not contain special characters.
            Ex: aBc1-DF51 xyz
          </>
        );
      case "already-exists":
        return duplicateVPCodeProductInfo ? (
          <>
            The VP Code entered already exists:{" "}
            <a
              className={styles.errorLink}
              href={duplicateVPCodeProductInfo.manufacturerProductUrl}
              target="_blank"
              rel="noopener noreferrer"
            >
              {duplicateVPCodeProductInfo.manufacturerProductTitle}
            </a>
            . Please revise your VP Code to continue.
          </>
        ) : undefined;
      default:
        return undefined;
    }
  })();

  const pb1MinQtyErrorMessage = (() => {
    switch (showPB1MinQtyError) {
      case "out-of-bounds":
        return "Minimum Quantity must be between 1 and 99.";
      case "greater-than-10":
        return (
          <>
            Minimum Quantity is greater than 10. Click{" "}
            <ConfirmationButton
              confirmationCallback={setConfirmationForGreaterThan10MinQty1}
              resetErrorStateCallback={setShowPB1MinQtyError}
            />{" "}
            if you want to use this minimum quantity.
          </>
        );
      case "required":
        return "Minimum Quantity 1 is required when Unit Price 1 is specified.";
      default:
        return undefined;
    }
  })();

  const unitPriceErrorMessage = (
    priceBreakNumber: 1 | 2,
    errorState: PriceBreak1UnitPriceErrorTypes | PriceBreak2UnitPriceErrorTypes,
    confirmationCallback: React.Dispatch<React.SetStateAction<boolean>>,
    resetErrorStateCallback: React.Dispatch<React.SetStateAction<any>>,
  ) => {
    switch (errorState) {
      case "required":
        return priceBreakNumber === 1
          ? "Please enter a valid unit price."
          : "Unit Price 2 is required when Minimum Quantity 2 is specified.";
      case "confirmation-required-under":
        return (
          <>
            Unit Price {priceBreakNumber} is more than 25% below the minimum selling price. Click{" "}
            <ConfirmationButton
              confirmationCallback={confirmationCallback}
              resetErrorStateCallback={resetErrorStateCallback}
            />{" "}
            if you want to sell at this price.
          </>
        );
      case "confirmation-required-over":
        return (
          <>
            Unit Price {priceBreakNumber} is more than 25% above the effective retail price. Click{" "}
            <ConfirmationButton
              confirmationCallback={confirmationCallback}
              resetErrorStateCallback={resetErrorStateCallback}
            />{" "}
            if you want to sell at this price.
          </>
        );
      case "too-far-below-lowest-price":
        return `Unit Price ${priceBreakNumber} cannot be more than 45% below the current retail price.`;
      case "too-far-over-retail":
        return `Unit Price ${priceBreakNumber} cannot be more than 45% above the current retail price.`;
      case "must-be-lower-than-pb1":
        return "Unit Price 2 must be less than Unit Price 1.";
      default:
        return undefined;
    }
  };

  const pb2MinQtyErrorMessage = (() => {
    switch (showPB2MinQtyError) {
      case "out-of-bounds":
        return "Minimum Quantity must be between 1 and 99.";
      case "minqty2-lower-than-minqty1":
        return "Minimum Quantity 2 must be greater than Minimum Quantity 1.";
      case "required":
        return "Minimum Quantity 2 is required when Unit Price 2 is specified.";
      default:
        return undefined;
    }
  })();

  const inventoryErrorMessage = (() => {
    switch (showInventoryError) {
      case "required":
        return "Your available inventory count is required.";
      case "out-of-bounds":
        return "Inventory must be between 1 and 9999.";
      default:
        return undefined;
    }
  })();

  const promisedHandlingTimeErrorMessage =
    promisedHandlingTimeError === "out-of-bounds" ? (
      <>
        Promised Handling Time must be a number between{" "}
        {fulfillmentPolicyMap.get(chosenFulfillmentPolicy)?.promisedHandlingTimeMinimum} and{" "}
        {fulfillmentPolicyMap.get(chosenFulfillmentPolicy)?.promisedHandlingTimeMaximum}.
      </>
    ) : undefined;

  const submitToBackend = async () => {
    try {
      const isFormValid = validateForm();
      if (!isFormValid) return;
      setDuplicateVPCodeProductInfo(null);
      setSubmittingToBackend(true);
      const saveWithWarnings: boolean =
        confirmationForGreaterThan10MinQty1 ||
        confirmationForGreaterThan25PercPrice1 ||
        confirmationForGreaterThan25PercPrice2;
      const fulfillmentPolicy: FulfillmentPolicyValue = fulfillmentPolicyMap.get(
        chosenFulfillmentPolicy,
      ) as FulfillmentPolicyValue;
      const postBody: SubmitSellThisNowFormRequestBody = {
        code: vendorProductCode.trim(),
        minQty1: parseInt(priceBreak1MinQty),
        price1: parseFloat(priceBreak1UnitPrice.replace(/[^0-9.]/gi, "")),
        minQty2: priceBreak2MinQty.trim() === "" ? undefined : parseInt(priceBreak2MinQty),
        price2:
          priceBreak2UnitPrice.trim() === "" ? undefined : parseFloat(priceBreak2UnitPrice.replace(/[^0-9.]/gi, "")),
        fulfillmentPoliceId: fulfillmentPolicy.id,
        promisedHandlingTime:
          editDefaultPHT === true ? parseInt(promisedHandlingTime) : fulfillmentPolicy.defaultPromisedHandlingTime,
        inventory: chosenFulfillmentPolicy == FulfillmentPolicy.STOCKED ? parseInt(inventory) : undefined,
      };
      const data = await addVendorProduct({ mpId, saveWithWarnings, postBody }).unwrap();
      setSubmittingToBackend(false);
      switch (data.callStatus) {
        case "SC_OK":
          onSubmitSuccess?.();
          setSubmissionSuccessful(true);
          setShowSellThisNowModal(false);
          dispatch(showSellThisNowToast());
          break;
        case "SC_ERROR_UNAUTHORIZED_USER":
          setShowSessionError(true);
          break;
        case "SC_ERROR_CODE_ALREADY_EXIST_FOR_VENDOR":
          setupDuplicateVendorProductInfo(data);
          break;
        case "SC_OK_WITH_WARNING":
        case "SC_ERROR_WITH_WARNINGS":
        case "SC_ERROR":
          if (data.warnings) {
            data.warnings.forEach((warning: any) => {
              switch (warning) {
                case "SC_WARNING_MIN_PRICE1_HIGH_DEVIATION":
                  setShowPB1UnitPriceError("confirmation-required-under");
                  break;
                case "SC_WARNING_MAX_PRICE1_HIGH_DEVIATION":
                  setShowPB1UnitPriceError("confirmation-required-over");
                  break;
                case "SC_WARNING_MIN_PRICE2_HIGH_DEVIATION":
                  setShowPB2UnitPriceError("confirmation-required-under");
                  break;
                case "SC_WARNING_MAX_PRICE2_HIGH_DEVIATION":
                  setShowPB2UnitPriceError("confirmation-required-over");
                  break;
              }
            });
          }
          if (data.errors) {
            data.errors.forEach((error: any) => {
              switch (error) {
                case "SC_ERROR_VENDOR_PRODUCT_CODE_MISSING":
                  setShowVPCodeError("invalid-format");
                  break;
                case "SC_ERROR_PRODUCT_CODE_ALREADY_EXIST":
                case "SC_ERROR_CODE_ALREADY_EXIST_FOR_VENDOR":
                  setupDuplicateVendorProductInfo(data);
                  break;
                case "SC_ERROR_INVALID_MINIMUM_QUANTITY1":
                  setShowPB1MinQtyError("required");
                  break;
                case "SC_ERROR_INVALID_PRICE1":
                  setShowPB1UnitPriceError("required");
                  break;
                case "SC_ERROR_INVALID_MINIMUM_QUANTITY2":
                  setShowPB2MinQtyError("required");
                  break;
                case "SC_ERROR_INVALID_PRICE2":
                  setShowPB1UnitPriceError("required");
                  break;
                case "SC_ERROR_MINIMUM_QUANTITY2_IS_LOWER_THAN_MINIMUM_QUANTITY1":
                  setShowPB2MinQtyError("minqty2-lower-than-minqty1");
                  break;
                case "SC_ERROR_PRICE1_IS_HIGHER_THAN_PRICE2":
                  setShowPB2UnitPriceError("must-be-lower-than-pb1");
                  break;
                case "SC_ERROR_TOO_LOW_MAXIMUM_PRICE1_DEVIATION":
                  setShowPB1UnitPriceError("confirmation-required-under");
                  break;
                case "SC_ERROR_TOO_HIGH_MAXIMUM_PRICE1_DEVIATION":
                  setShowPB1UnitPriceError("confirmation-required-over");
                  break;
                case "SC_ERROR_TOO_LOW_MAXIMUM_PRICE2_DEVIATION":
                  setShowPB2UnitPriceError("confirmation-required-under");
                  break;
                case "SC_ERROR_TOO_HIGH_MAXIMUM_PRICE2_DEVIATION":
                  setShowPB2UnitPriceError("confirmation-required-over");
                  break;
                case "SC_ERROR_HANDLING_TIME_TOO_LOW":
                case "SC_ERROR_PROMISED_HANDLING_TIME_MISSING":
                case "SC_ERROR_HANDLING_TIME_TOO_HIGH":
                  setPromisedHandlingTimeError("out-of-bounds");
                  break;
                case "SC_ERROR_INVALID_INVENTORY":
                  setShowInventoryError("required");
                  break;
              }
            });
          }
          break;
        default:
          console.error("Sell this Now submitToBackend - Backend returned call status of ", data.callStatus);
          setShowUnknownError(true);
          break;
      }
    } catch (error) {
      console.error(`Sell this Now submitToBackend threw an exception:`, error);
      setShowUnknownError(true);
    }
  };

  const setupDuplicateVendorProductInfo = (data: SubmitSellThisNowFormResponse) => {
    if (data.manufacturerProductTitle && data.manufacturerProductUrl) {
      setShowVPCodeError("already-exists");
      setDuplicateVPCodeProductInfo({
        manufacturerProductTitle: data.manufacturerProductTitle,
        manufacturerProductUrl:
          "/" +
          (data.manufacturerProductUrl.indexOf("/") === 0
            ? data.manufacturerProductUrl.slice(1)
            : data.manufacturerProductUrl),
      });
    } else {
      setShowUnknownError(true);
    }
  };

  /**
   * Verifies everything in the form, and sets error state variables to display errors.
   * @returns true if the entire form is valid or false if something is incorrect.
   */
  const validateForm = (): boolean => {
    let formVerified = true;
    setFormWasValidated(true);
    setShowVPCodeError("none");
    setShowPB1MinQtyError("none");
    setShowPB1UnitPriceError("none");
    setShowPB2MinQtyError("none");
    setShowPB2UnitPriceError("none");
    setShowInventoryError("none");
    setPromisedHandlingTimeError("none");

    const trimmedVPCode = vendorProductCode.trim();
    if (trimmedVPCode === "" || trimmedVPCode !== trimmedVPCode.replace(/[^0-9a-z\- ]/gi, "")) {
      formVerified = false;
      setShowVPCodeError("invalid-format");
    }

    const pb1MinQtyState = verifyMinQty(priceBreak1MinQty, confirmationForGreaterThan10MinQty1);
    if (pb1MinQtyState !== "none") {
      formVerified = false;
      setShowPB1MinQtyError(pb1MinQtyState);
    }
    const pb1UnitPriceState = verifyPriceBreak(priceBreak1UnitPrice, confirmationForGreaterThan25PercPrice1);
    if (pb1UnitPriceState !== "none") {
      formVerified = false;
      setShowPB1UnitPriceError(pb1UnitPriceState);
    }

    // The following means they have supplies one of these things and so they need to be checked
    if (priceBreak2MinQty.trim() !== "" || priceBreak2UnitPrice.trim() !== "") {
      const pb2MinQtyState = verifyMinQty(priceBreak2MinQty);
      // For minqty 2 we want to ignore the "greater-than-10" error
      if (pb2MinQtyState === "out-of-bounds") {
        formVerified = false;
        setShowPB2MinQtyError(pb2MinQtyState);
      } else {
        const minqty1 = parseInt(priceBreak1MinQty);
        const minqty2 = parseInt(priceBreak2MinQty);
        if (isNaN(minqty1) === false && isNaN(minqty2) === false && minqty1 >= minqty2) {
          formVerified = false;
          setShowPB2MinQtyError("minqty2-lower-than-minqty1");
        }
      }
      const pb2UnitPriceState = verifyPriceBreak(priceBreak2UnitPrice, confirmationForGreaterThan25PercPrice2);
      if (pb2UnitPriceState !== "none") {
        formVerified = false;
        setShowPB2UnitPriceError(pb2UnitPriceState);
      } else {
        // Try/catch is for BigDecimal throwing isNaN error
        try {
          const pb1 = new BigDecimal(priceBreak1UnitPrice.replace(/[^0-9.]/gi, ""));
          const pb2 = new BigDecimal(priceBreak2UnitPrice.replace(/[^0-9.]/gi, ""));
          if (pb2.gte(pb1)) {
            formVerified = false;
            setShowPB2UnitPriceError("must-be-lower-than-pb1");
          }
        } catch (error) {
          console.error(error);
        }
      }
    }

    if (chosenFulfillmentPolicy === FulfillmentPolicy.STOCKED) {
      const trimmedInventory = inventory.trim();
      if (trimmedInventory === "") {
        formVerified = false;
        setShowInventoryError("required");
      } else {
        const inventoryNum = parseInt(inventory);
        if (inventoryNum < 1 || inventoryNum > 9999) {
          formVerified = false;
          setShowInventoryError("out-of-bounds");
        }
      }
    }

    if (editDefaultPHT === true) {
      const { promisedHandlingTimeMinimum, promisedHandlingTimeMaximum } = fulfillmentPolicyMap.get(
        chosenFulfillmentPolicy,
      ) as FulfillmentPolicyValue;
      const phtNumber = parseInt(promisedHandlingTime);
      if (isNaN(phtNumber) || phtNumber < promisedHandlingTimeMinimum || phtNumber > promisedHandlingTimeMaximum) {
        formVerified = false;
        setPromisedHandlingTimeError("out-of-bounds");
      }
    }

    return formVerified;
  };

  /**
   * Checks the supplied minimum quantity to see if it is valid.
   * @param {string} pbMinQtyString
   * @param {boolean} receivedConfirmation If explicitely false, it will test if min qty is over 10. If true or undefined (for minqty2), the test will not be performed
   * @returns "none" if no error is found, or the error type.
   */
  function verifyMinQty(pbMinQtyString: string, receivedConfirmation?: boolean): PriceBreakMinQtyErrorTypes {
    const pbMinQty = pbMinQtyString.trim() === "" ? 0 : parseInt(pbMinQtyString);
    if (pbMinQty < 1 || pbMinQty > 99) {
      return "out-of-bounds";
    } else if (receivedConfirmation === false && pbMinQty > 10) {
      return "greater-than-10";
    }
    return "none";
  }

  /**
   * Checks the supplied price break to see if it is valid.
   * @param {string} pbUnitPriceString
   * @param {boolean} receivedConfirmation
   * @returns "none" if no error is found, or the error type.
   */
  function verifyPriceBreak(pbUnitPriceString: string, receivedConfirmation: boolean): PriceBreak1UnitPriceErrorTypes {
    // The try/catch here is catch if BigDecimal throws a NaN error
    try {
      const vendorPrices = vendorOptions.map((vendorOption) => {
        const prices: Array<BigDecimal> = [];
        vendorOption.priceBreaks.forEach((priceBreak) => {
          prices.push(Big(priceBreak.unitPrice));
        });
        return prices.sort((price1, price2) => {
          if (price1.gt(price2)) return 1;
          else if (price2.gt(price1)) return -1;
          else return 0;
        })[0];
      });
      const vendorPricesSortedAscending = vendorPrices.sort((price1, price2) => {
        if (price1.gt(price2)) return 1;
        else if (price2.gt(price1)) return -1;
        else return 0;
      });
      pbUnitPriceString = pbUnitPriceString.replace(/[^0-9.]/gi, "");
      const pbUnitPrice = new BigDecimal(pbUnitPriceString);
      if (pbUnitPrice.eq(0)) return "required";
      if (vendorPricesSortedAscending.length === 0 && !(retailPrice > 0)) return "none";

      const calculatedRetailPrice = ((): BigDecimal => {
        if (vendorPricesSortedAscending.length === 0) return Big(retailPrice);
        const highestPriceVendorPrice = [...vendorPricesSortedAscending].reverse()[0];
        if (highestPriceVendorPrice.gt(retailPrice)) return highestPriceVendorPrice;
        else return Big(retailPrice);
      })();

      if (pbUnitPrice.gt(calculatedRetailPrice.times("1.45"))) {
        return "too-far-over-retail";
      } else if (pbUnitPrice.gt(calculatedRetailPrice.times("1.25")) && receivedConfirmation === false) {
        return "confirmation-required-over";
      } else if (vendorPricesSortedAscending && vendorPricesSortedAscending.length > 0) {
        const lowestPrice = vendorPricesSortedAscending[0];

        if (pbUnitPrice.lt(lowestPrice.times("0.45"))) {
          return "too-far-below-lowest-price";
        } else if (pbUnitPrice.lt(lowestPrice.times("0.75")) && receivedConfirmation === false) {
          return "confirmation-required-under";
        }
      }
      return "none";
    } catch (error) {
      console.error("Sell this now | verifyPriceBreak threw an exception: ", error);
      return "required";
    }
  }

  const defaultPromisedHandlingTime = fulfillmentPolicyMap.get(chosenFulfillmentPolicy)?.defaultPromisedHandlingTime;

  return (
    <div id="sell-this-now">
      {!submissionSuccessful && (
        <Button
          variant="tertiary"
          color="blue"
          size={triggerSize}
          text="Want to sell this product?"
          showLeftIcon={false}
          showRightIcon={false}
          onClick={() => setShowSellThisNowModal(true)}
        />
      )}
      <Modal
        size="skinny"
        sizeDownMd="medium"
        show={showSellThisNowModal}
        onHide={() => setShowSellThisNowModal(false)}
        title="Sell This Product Now"
        footer={
          <>
            <Button
              variant="secondary"
              color="blue"
              size="large"
              text="Cancel"
              showLeftIcon={false}
              showRightIcon={false}
              disabled={submittingToBackend}
              onClick={() => setShowSellThisNowModal(false)}
            />
            <Button
              variant="primary"
              color="blue"
              size="large"
              text="Submit"
              showLeftIcon={false}
              showRightIcon={false}
              loading={submittingToBackend}
              disabled={submittingToBackend}
              onClick={() => sellThisNowFormRef.current?.requestSubmit()}
            />
          </>
        }
      >
        <div className={styles.content}>
          <p className={styles.description}>
            Please fill out the required fields below and click &ldquo;Submit&rdquo;.
          </p>
          {(showSessionError || showUnknownError) && (
            <Alert
              variant="error"
              size="small"
              role="alert"
              message={
                showSessionError ? (
                  <>
                    Your session has timed out. For security reasons,{" "}
                    <a href="/login" target="_blank" rel="noopener noreferrer">
                      log in
                    </a>{" "}
                    again.
                  </>
                ) : (
                  "An unknown error occurred."
                )
              }
            />
          )}
          <form
            ref={sellThisNowFormRef}
            className={`${styles.form} ${formWasValidated ? "was-validated" : ""}`}
            onSubmit={(event) => {
              event.preventDefault();
              submitToBackend();
            }}
            autoComplete="off"
            noValidate
          >
            <TextInputField
              id="stn-vp-code"
              label="Vendor Product Code (VP Code)"
              required
              size="medium"
              showLeftIcon={false}
              showRightIcon={false}
              placeholder="Ex. aBc-123 DF51 (no special characters)"
              value={vendorProductCode}
              onChange={(e) => setVendorProductCode(e.target.value)}
              error={showVPCodeError !== "none"}
              errorMessage={vpCodeErrorMessage}
              disabled={submittingToBackend}
            />
            <div className={styles.fieldRow}>
              <NumericFormat
                customInput={TextInputField}
                id="stn-price-break-1-min-qty"
                label="Minimum Quantity 1"
                required
                size="medium"
                showLeftIcon={false}
                showRightIcon={false}
                value={priceBreak1MinQty}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  setPriceBreak1MinQty(e.target.value);
                  setConfirmationForGreaterThan10MinQty1(false);
                }}
                error={showPB1MinQtyError !== "none"}
                errorMessage={pb1MinQtyErrorMessage}
                disabled={submittingToBackend}
                allowNegative={false}
              />
              <NumericFormat
                customInput={TextInputField}
                id="stn-price-break-1-unit-price"
                label="Unit Price 1"
                required
                size="medium"
                showLeftIcon={false}
                showRightIcon={false}
                thousandSeparator={true}
                decimalScale={2}
                fixedDecimalScale={true}
                value={priceBreak1UnitPrice}
                error={showPB1UnitPriceError !== "none"}
                errorMessage={unitPriceErrorMessage(
                  1,
                  showPB1UnitPriceError,
                  setConfirmationForGreaterThan25PercPrice1,
                  setShowPB1UnitPriceError,
                )}
                disabled={submittingToBackend}
                allowNegative={false}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  setPriceBreak1UnitPrice(e.target.value);
                  setConfirmationForGreaterThan25PercPrice1(false);
                }}
              />
            </div>
            <div className={styles.fieldRow}>
              <NumericFormat
                customInput={TextInputField}
                id="stn-price-break-2-min-qty"
                label="Minimum Quantity 2"
                size="medium"
                showLeftIcon={false}
                showRightIcon={false}
                value={priceBreak2MinQty}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setPriceBreak2MinQty(e.target.value)}
                error={showPB2MinQtyError !== "none"}
                errorMessage={pb2MinQtyErrorMessage}
                disabled={submittingToBackend}
                allowNegative={false}
              />
              <NumericFormat
                customInput={TextInputField}
                id="stn-price-break-2-unit-price"
                label="Unit Price 2"
                size="medium"
                showLeftIcon={false}
                showRightIcon={false}
                thousandSeparator={true}
                decimalScale={2}
                fixedDecimalScale={true}
                value={priceBreak2UnitPrice}
                error={showPB2UnitPriceError !== "none"}
                errorMessage={unitPriceErrorMessage(
                  2,
                  showPB2UnitPriceError,
                  setConfirmationForGreaterThan25PercPrice2,
                  setShowPB2UnitPriceError,
                )}
                allowNegative={false}
                disabled={submittingToBackend}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  setPriceBreak2UnitPrice(e.target.value);
                  setConfirmationForGreaterThan25PercPrice2(false);
                }}
              />
            </div>
            <div className={styles.fieldRow}>
              <div className={styles.fulfillmentPolicyField}>
                <div className={styles.fulfillmentPolicyLabel}>
                  <label htmlFor="stn-fulfillment-policy">
                    Fulfillment Policy<span className={styles.requiredMark}>*</span>
                  </label>
                  <Tooltip
                    id="stn-fulfillment-policy-help"
                    tailPlacement="above"
                    content={
                      <div className={styles.fulfillmentPolicyExplanation}>
                        <strong>Fulfillment policy defaults</strong>
                        <p>
                          Each fulfillment type has its own default <strong>Promised Handling Time (PHT)</strong>. Yours
                          are currently: Stock{" "}
                          {fulfillmentPolicyMap.get(FulfillmentPolicy.STOCKED)?.defaultPromisedHandlingTime}, MFR Order{" "}
                          {fulfillmentPolicyMap.get(FulfillmentPolicy.MFR_ORDER)?.defaultPromisedHandlingTime}, and Drop
                          Ship {fulfillmentPolicyMap.get(FulfillmentPolicy.DROP_SHIP)?.defaultPromisedHandlingTime}{" "}
                          business days.
                        </p>
                        <p>
                          Click the number of days to enter a custom PHT, or &ldquo;Revert to default&rdquo; to use the
                          default value.
                        </p>
                      </div>
                    }
                  >
                    <button
                      type="button"
                      className={styles.helpButton}
                      aria-label="Fulfillment policy help"
                      aria-describedby="stn-fulfillment-policy-help"
                    >
                      <InfoIcon width={16} height={16} aria-hidden />
                    </button>
                  </Tooltip>
                </div>
                <Dropdown
                  id="stn-fulfillment-policy"
                  value={chosenFulfillmentPolicy.toString()}
                  disabled={submittingToBackend}
                  onChange={(e) => {
                    setChosenFulfillmentPolicy(parseInt(e.target.value) as FulfillmentPolicy);
                    setShowInventoryError("none");
                  }}
                >
                  <option value={FulfillmentPolicy.STOCKED}>Stock</option>
                  <option value={FulfillmentPolicy.MFR_ORDER}>MFR Order</option>
                  <option value={FulfillmentPolicy.DROP_SHIP}>Drop Ship</option>
                </Dropdown>
              </div>
              {chosenFulfillmentPolicy === FulfillmentPolicy.STOCKED ? (
                <NumericFormat
                  customInput={TextInputField}
                  id="stn-inventory"
                  label="Inventory"
                  required
                  size="medium"
                  showLeftIcon={false}
                  showRightIcon={false}
                  value={inventory}
                  error={showInventoryError !== "none"}
                  errorMessage={inventoryErrorMessage}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setInventory(e.target.value)}
                  disabled={submittingToBackend}
                  allowNegative={false}
                />
              ) : (
                <div className={styles.fieldSpacer} />
              )}
            </div>
            <div className={styles.fieldRow}>
              <NumericFormat
                customInput={TextInputField}
                id="stn-promised-handling-time"
                label="Promised Handling Time (PHT)"
                required
                size="medium"
                showLeftIcon={false}
                showRightIcon={false}
                value={editDefaultPHT ? promisedHandlingTime : (defaultPromisedHandlingTime?.toString() ?? "")}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  setEditDefaultPHT(true);
                  setPromisedHandlingTime(e.target.value.replace(/[^0-9]/g, ""));
                }}
                error={editDefaultPHT && promisedHandlingTimeError !== "none"}
                errorMessage={promisedHandlingTimeErrorMessage}
                disabled={submittingToBackend}
                allowNegative={false}
              />
              {!editDefaultPHT ? (
                <div className={styles.phtDefaultNote}>(Default used)</div>
              ) : preventDefaultPHTFromDisplaying === false && defaultPromisedHandlingTime !== undefined ? (
                <div className={styles.phtRevert}>
                  <Button
                    variant="tertiary"
                    color="blue"
                    size="large"
                    text={`Use Default (${defaultPromisedHandlingTime})`}
                    showLeftIcon={false}
                    showRightIcon={false}
                    disabled={submittingToBackend}
                    onClick={() => {
                      setEditDefaultPHT(false);
                      setPromisedHandlingTime(defaultPromisedHandlingTime.toString());
                      setPromisedHandlingTimeError("none");
                    }}
                  />
                </div>
              ) : (
                <div className={styles.fieldSpacer} />
              )}
            </div>
          </form>
        </div>
      </Modal>
      <Toast
        toastMessage="Product Now for Sale"
        toastSubMessage="Your product is now for sale. Your price(s) should appear on this page shortly."
        showToast={showSuccessToast}
        toastType="success"
        hideToast={hideSellThisNowToast}
        showCloseButton
        showOnMobile
        showToastTime={6000}
        toastLocation="top"
      />
    </div>
  );
};

export default SellThisNow;

type VPCodeError = "none" | "invalid-format" | "already-exists";

type PriceBreakMinQtyErrorTypes = "none" | "out-of-bounds" | "greater-than-10" | "required";

type PriceBreak1UnitPriceErrorTypes =
  | "none"
  | "required"
  | "confirmation-required-under"
  | "confirmation-required-over"
  | "too-far-over-retail"
  | "too-far-below-lowest-price";

type PriceBreak2UnitPriceErrorTypes = PriceBreak1UnitPriceErrorTypes | "must-be-lower-than-pb1";

type InventoryError = "none" | "required" | "out-of-bounds";

type PHTError = "none" | "out-of-bounds";

type DuplicateProductInfo = {
  manufacturerProductTitle: string;
  manufacturerProductUrl: string;
};

export type SubmitSellThisNowFormRequestBody = {
  /** vendor product code */
  code: string;
  minQty1: number;
  price1: number;
  minQty2?: number;
  price2?: number;
  fulfillmentPoliceId: number;
  promisedHandlingTime: number;
  /** required only if fulfillmentPoliceId is 1, aka stocked */
  inventory?: number;
};
export type SubmitSellThisNowFormResponse = {
  callStatus:
    | "SC_OK"
    | "SC_ERROR"
    | "SC_ERROR_UNAUTHORIZED_USER"
    | "SC_OK_WITH_WARNING"
    | "SC_ERROR_WITH_WARNINGS"
    | "SC_ERROR_CODE_ALREADY_EXIST_FOR_VENDOR";
  warnings?: Array<
    | "SC_WARNING_MIN_PRICE1_HIGH_DEVIATION"
    | "SC_WARNING_MAX_PRICE1_HIGH_DEVIATION"
    | "SC_WARNING_MIN_PRICE2_HIGH_DEVIATION"
    | "SC_WARNING_MAX_PRICE2_HIGH_DEVIATION"
  >;
  errors?: Array<
    | "SC_ERROR_VENDOR_PRODUCT_CODE_MISSING"
    | "SC_ERROR_PRODUCT_CODE_ALREADY_EXIST"
    | "SC_ERROR_CODE_ALREADY_EXIST_FOR_VENDOR"
    | "SC_ERROR_INVALID_MINIMUM_QUANTITY1"
    | "SC_ERROR_INVALID_PRICE1"
    | "SC_ERROR_MINIMUM_QUANTITY2_IS_LOWER_THAN_MINIMUM_QUANTITY1"
    | "SC_ERROR_PRICE1_IS_HIGHER_THAN_PRICE2"
    | "SC_ERROR_INVALID_MINIMUM_QUANTITY2"
    | "SC_ERROR_INVALID_PRICE2"
    | "SC_ERROR_TOO_LOW_MAXIMUM_PRICE1_DEVIATION"
    | "SC_ERROR_TOO_HIGH_MAXIMUM_PRICE1_DEVIATION"
    | "SC_ERROR_TOO_LOW_MAXIMUM_PRICE2_DEVIATION"
    | "SC_ERROR_TOO_HIGH_MAXIMUM_PRICE2_DEVIATION"
    | "SC_ERROR_FULFILLMENT_POLICE_MISSING"
    | "SC_ERROR_INVALID_FP_ID"
    | "SC_ERROR_HANDLING_TIME_TOO_LOW"
    | "SC_ERROR_HANDLING_TIME_TOO_HIGH"
    | "SC_ERROR_INVALID_INVENTORY"
    | "SC_ERROR_PROMISED_HANDLING_TIME_MISSING"
  >;
  manufacturerProductTitle?: string;
  manufacturerProductUrl?: string;
};

type ConfirmationButtonProps = {
  confirmationCallback: React.Dispatch<React.SetStateAction<boolean>>;
  /** The "any" here is a bit dangerous.  It needs to always accept at least "none" */
  resetErrorStateCallback: React.Dispatch<React.SetStateAction<any>>;
};

type FulfillmentPolicyValue = {
  id: number;
  defaultPromisedHandlingTime: number;
  promisedHandlingTimeMinimum: number;
  promisedHandlingTimeMaximum: number;
};

enum FulfillmentPolicy {
  STOCKED = 1,
  MFR_ORDER = 2,
  DROP_SHIP = 3,
}
const ConfirmationButton: FunctionComponent<ConfirmationButtonProps> = ({
  confirmationCallback,
  resetErrorStateCallback,
}) => {
  return (
    <button
      type="button"
      className={styles.confirmButton}
      onClick={() => {
        confirmationCallback(true);
        resetErrorStateCallback("none");
      }}
    >
      Confirm
    </button>
  );
};
